/* eslint-disable react/no-array-index-key */
import {SerializedStyles} from '@emotion/react';
import {Collapse, css, useTheme} from '@mui/material';
import {
  ApplicationInfo,
  ArrayUtils,
  ConfigObjectType,
  DlMuiButton,
  DlMuiDynamicIcon,
  DlMuiTextField,
  GetStylesFunction,
  IconHeaderWithSearch,
  LightIconButton,
  LoadingSpinner,
  omitEmptyDeep,
  omitNullsy,
  PopupWrapper,
  StringUtils,
  useApiMutation,
  useApiQuery,
  useCurrentLocale,
  useLocalStorage,
} from '@wallaby-ui/core';
import React, {useCallback, useMemo, useState} from 'react';

import {LightCheckbox} from './LightCheckbox';
import {Formatters, useFormatters} from './LightFormatter';
import {LightSavedFilterAlertBell} from './LightSavedFilterAlertBell';
import {HaidiSchema} from '../../types';

/* To add saved filters box:
Split <Page> into:
  <Page> with useUserFilters()
  <PageInner> with GET data
Add <div css={muiStyles.filtersScrollBox}> around <Collapse /> filters
Add <LightSavedFiltersBox />
Remove <DlMuiToggleFilters />
Add or replace .splitContentBox, (.filtersWrapper, .filtersBox, .filtersScrollBox, .filterInputsWrapper) styles
Remove .savedFiltersBox styles
Change split sizes to [124, 836]?
*/

type UserFilterAllFilterDto = HaidiSchema.components['schemas']['UserFilterAllFilterDto'];
/** The alert state of a saved filter (PDW-242): a filter that never was an alert has none. RETIRED renders like
 * none on the row (the difference matters on the Alert board only). */
export type UserFilterAlertState = 'ACTIVE' | 'RETIRED';
/** Shared-filters tier fields (PDW-157/PDW-159) and the alert fields (PDW-242) stay optional until the backend
 * schema is regenerated. A global filter is a row owned by a technical user on the BE; `isGlobal` is computed there. */
export type UserFilterDto = HaidiSchema.components['schemas']['UserFilterDto'] & {
  isGlobal?: boolean;
  sharedBy?: string;
  /** Null for a filter that never was an alert, and for every filter of a user without the Use alerts right. */
  alertState?: UserFilterAlertState | null;
  /** 0 to 100, set exactly when alertState is. */
  weight?: number | null;
  /** Whether this user may mark, reweight or retire the filter's alert - decided by the BE, never derived here. */
  canManage?: boolean;
};
type UserFilterCreateDto = HaidiSchema.components['schemas']['UserFilterCreateDto'];
type UseSavedFiltersProps = {
  storageIdPrefix: string;
  component: string;
  config: ConfigObjectType;
  changeFilters: (filterDiff: Record<string, any>) => void;
};
type UseSavedFiltersReturn = {
  waitForUserFilters: boolean;
  userFiltersLoading: boolean;
  addedUserFilters: UserFilterDto[];
  userFilters: UserFilterDto[];
  changeUserFilter: (newUserFilter: UserFilterDto, toDelete?: boolean) => void;
  refetchUserFilters: () => void;
};
export function useUserFilters(props: UseSavedFiltersProps): UseSavedFiltersReturn {
  const {storageIdPrefix, component, config, changeFilters} = props;
  const haveFiltersInQuery = Boolean(new URLSearchParams(window.location.search).get(`${storageIdPrefix}.LQ`));
  const [usedDefaultFilter, _, setUsedDefaultFilter] = useLocalStorage(`${storageIdPrefix}.usedDefaultFilter`, false);
  const waitForUserFilters = !usedDefaultFilter && !haveFiltersInQuery;
  const userFilterGetParams = new URLSearchParams(omitNullsy({component} as UserFilterAllFilterDto));
  const userFilterGetUrl = config.service.url.userFilterGet;
  const [addedUserFilters, setAddedUserFilters] = useState([] as UserFilterDto[]);
  const [userFilters, setUserFilters] = useState([] as UserFilterDto[]);
  const onSuccess = useCallback(
    (response: UserFilterDto[]) => {
      setAddedUserFilters([]);
      setUserFilters(response);
      if (!usedDefaultFilter) {
        if (waitForUserFilters) {
          const defaultUserFilter = response.find((v) => v.isDefault);
          try {
            if (defaultUserFilter) changeFilters(JSON.parse(defaultUserFilter.data));
          } catch {
            // noop
          }
        }
        setTimeout(() => setUsedDefaultFilter(true));
      }
    },
    [usedDefaultFilter, setUsedDefaultFilter, waitForUserFilters, changeFilters]
  );
  const {
    isLoading: dataLoading,
    isRefetching: dataRefetching,
    refetch: refetchUserFilters,
  } = useApiQuery<UserFilterDto[]>([userFilterGetUrl, String(userFilterGetParams)], null, {
    axiosConfig: {
      url: userFilterGetUrl,
      params: userFilterGetParams,
    },
    reactQueryOptions: {
      staleTime: 0,
      refetchOnMount: 'always',
      onSuccess,
    },
  });
  const changeUserFilter = useCallback(
    (newUserFilter: UserFilterDto, toDelete?: boolean) => {
      // Functional updates only: share/revert call this twice back-to-back (drop one row, upsert the
      // other), so captured arrays would be stale on the second call and resurrect the dropped row.
      const clearOtherDefaults = (arr: UserFilterDto[]) => (newUserFilter.isDefault ? arr.map((v) => ({...v, isDefault: false})) : arr);
      if (toDelete) {
        if (!newUserFilter.id) return;
        setAddedUserFilters((prev) => prev.filter((v) => v.id !== newUserFilter.id));
        setUserFilters((prev) => prev.filter((v) => v.id !== newUserFilter.id));
        return;
      }
      let inUserFilters = false;
      setUserFilters((prev) => {
        const userFilterIndex = prev.findIndex((v) => v.id === newUserFilter.id);
        inUserFilters = userFilterIndex !== -1;
        const newUserFilters = clearOtherDefaults([...prev]);
        if (userFilterIndex !== -1) newUserFilters.splice(userFilterIndex, 1, newUserFilter);
        return newUserFilters;
      });
      // the setUserFilters updater above runs first in the same commit, so inUserFilters is decided here
      setAddedUserFilters((prev) => {
        const addedUserFilterIndex = prev.findIndex((v) => v.id === newUserFilter.id);
        const newAddedUserFilters = clearOtherDefaults([...prev]);
        if (addedUserFilterIndex !== -1) {
          newAddedUserFilters.splice(addedUserFilterIndex, 1, newUserFilter);
          return newAddedUserFilters;
        }
        return inUserFilters ? newAddedUserFilters : [newUserFilter, ...newAddedUserFilters];
      });
    },
    []
  );
  const locale = useCurrentLocale();
  const userFiltersSorted = useMemo(() => {
    return ArrayUtils.sortedByLocaleString(userFilters, (v) => v.name, locale, {caseFirst: 'lower'});
  }, [userFilters, locale]);
  return {
    waitForUserFilters,
    userFiltersLoading: dataLoading || dataRefetching,
    addedUserFilters,
    userFilters: userFiltersSorted,
    changeUserFilter,
    refetchUserFilters,
  };
}

type Props = {
  open: boolean;
  toggleOpen: () => void;
  storageIdPrefix: string;
  component: string;
  userFiltersLoading: boolean;
  addedUserFilters: UserFilterDto[];
  userFilters: UserFilterDto[];
  changeUserFilter: (newUserFilter: UserFilterDto, toDelete?: boolean) => void;
  filter: Record<string, any>;
  setFilter: (newFilter: Record<string, any>) => void;
  config: ConfigObjectType;
  limitHeight?: boolean;
  /**
   * Detach the panel from the card's height calculation: its content is absolutely positioned so it no
   * longer contributes to the flex-row's natural height. The card then follows ONLY the filters height and
   * this panel fills that height and scrolls internally — so the number of saved filters can't grow the card
   * (fixes the gap-under-filters on full-expand report pages). Only for the side-by-side filters+saved layout.
   */
  fitToFilters?: boolean;
  savedFiltersScrollBoxStyles?: React.CSSProperties;
  normalizeForCompare?: (filter: Record<string, any>) => Record<string, any>;
  normalizeForStore?: (filter: Record<string, any>) => Record<string, any>;
  /** When true the toggle icon is hidden, the icon+label in the header are hidden, and the content is always expanded (intended for popover/dropdown usage). */
  isPopupMode?: boolean;
  /**
   * Single-scroll layout mode: the header is NOT rendered here (the page renders it separately via
   * LightSavedFiltersHeader in a fixed header row) and the content does not scroll internally — it flows
   * at its natural height inside the page's shared scroll area. Content visibility still follows `open`.
   */
  detachedHeader?: boolean;
  /**
   * Whether any filter is currently active (i.e. differs from the default empty state).
   * When provided it overrides the internal fallback `filterString !== '{}'`, which is
   * incorrect for pages whose default filter is non-empty (e.g. contains preset enum values).
   */
  hasActiveFilter?: boolean;
  /**
   * Called after a saved filter's alert was set, reweighted or retired, so the page reloads the data that shows its
   * alerts (the list's row bells); the box itself only updates its own row.
   */
  onAlertsChanged?: () => void;
};
export const FILTER_ACTIVE_ICON_COLORS = {
  default: {background: 'var(--dlFilterActiveIconBackground)'},
  hover: {background: 'var(--dlFilterActiveIconHoverBackground)'},
};

/**
 * The filter without the values that filter nothing. MERIE reads a saved filter strictly as its filter DTO (PDW-393), so
 * an empty list, a blank string or an empty nested object is left out at every depth, on save and on compare alike.
 * HAIDI keeps its stored shape, nulls dropped at the top level only, until it gets alerts.
 */
function omitFilterlessValues(filter: Record<string, any>): Record<string, any> {
  return ApplicationInfo.getAppName() === 'MERIE' ? omitEmptyDeep(filter) : omitNullsy(filter);
}

export function toComparableString(
  raw: Record<string, any>,
  normalizeForCompare?: (filter: Record<string, any>) => Record<string, any>
): string {
  const normalized = normalizeForCompare ? normalizeForCompare(raw) : raw;
  return StringUtils.stringifyStable(omitFilterlessValues(normalized));
}

/** The confirmed actions on a saved filter: the shared-filters tier ones, and every delete (PDW-408), worded apart for a
 * filter that is also an alert, which goes with it. */
type SharedFilterConfirmAction = 'share' | 'revert' | 'delete' | 'deleteAlert' | 'deleteShared' | 'deleteSharedAlert';
type SharedFilterConfirmState = {action: SharedFilterConfirmAction; userFilter: UserFilterDto; anchorEl: HTMLElement};
/**
 * Dialog body texts. Both are complete translated sentences carrying the `<filter_name>`
 * placeholder (position differs per language): `pre` is an optional lead line shown above
 * the body `post`. Rendered via [renderWithFilterName], like the title.
 */
type ConfirmParagraph = {pre?: string; post: string};
const SHARED_FILTER_CONFIRM_COPY: Record<
  SharedFilterConfirmAction,
  {title: string; paragraph: ConfirmParagraph; cannotUndo?: boolean; confirm: string; destructive?: boolean}
> = {
  delete: {
    title: 'filter.user.delete.title',
    paragraph: {post: 'filter.user.delete.text.post'},
    cannotUndo: true,
    confirm: 'filter.user.delete.confirm',
    destructive: true,
  },
  deleteAlert: {
    title: 'filter.user.deleteAlert.title',
    paragraph: {post: 'filter.user.deleteAlert.text.post'},
    cannotUndo: true,
    confirm: 'filter.user.delete.confirm',
    destructive: true,
  },
  deleteSharedAlert: {
    title: 'filter.user.deleteShared.title',
    paragraph: {pre: 'filter.user.deleteSharedAlert.text.pre', post: 'filter.user.deleteSharedAlert.text.post'},
    cannotUndo: true,
    confirm: 'filter.user.deleteShared.confirm',
    destructive: true,
  },
  share: {
    title: 'filter.user.share.title',
    paragraph: {pre: 'filter.user.share.text.pre', post: 'filter.user.share.text.post'},
    confirm: 'filter.user.share.confirm',
  },
  revert: {
    title: 'filter.user.revert.title',
    paragraph: {post: 'filter.user.revert.text.post'},
    confirm: 'filter.user.revert.confirm',
  },
  deleteShared: {
    title: 'filter.user.deleteShared.title',
    paragraph: {pre: 'filter.user.deleteShared.text.pre', post: 'filter.user.deleteShared.text.post'},
    cannotUndo: true,
    confirm: 'filter.user.deleteShared.confirm',
    destructive: true,
  },
};

const FILTER_NAME_PLACEHOLDER = '<filter_name>';

/** Replaces every `<filter_name>` placeholder in the translated text with the bold filter name. */
function renderWithFilterName(text: string, name: string) {
  const parts = text.split(FILTER_NAME_PLACEHOLDER);
  return parts.flatMap((part, index) => (index === 0 ? [part] : [<strong key={index}>{name}</strong>, part]));
}

/** Global filter management permission (IDEA-283, Keycloak role ROLE_<APP>_GLOBAL_FILTER_EDIT).
 * Reads the same /api/permissions query usePermissions uses, so react-query dedupes the request;
 * kept local to avoid a light -> containers import cycle. */
function useCanShareFilters(config: ConfigObjectType): boolean {
  const permissionsUrl = config.service.url.permissions;
  const {data} = useApiQuery<{roles?: string[] | null}>([permissionsUrl], null, {
    axiosConfig: {url: permissionsUrl},
  });
  return Boolean(data?.roles?.includes('GLOBAL_FILTER_EDIT_ROLE'));
}

type SavedFiltersHeaderProps = {
  open: boolean;
  toggleOpen: () => void;
  hasActiveFilter?: boolean;
  /** Extra header buttons rendered after the filters toggle (e.g. the maximize button of a tabbed panel). */
  children?: React.ReactNode;
};
/**
 * The "Saved filters" header (icon + label + filters-toggle icon) rendered standalone — pair with
 * LightSavedFiltersBox `detachedHeader` in single-scroll filter panels where the headers stay fixed
 * above a shared scroll area.
 */
export const LightSavedFiltersHeader: React.FC<SavedFiltersHeaderProps> = (props) => {
  const {open, toggleOpen, hasActiveFilter, children} = props;
  const theme = useTheme();
  const muiStyles = getStyles({theme});
  return (
    <IconHeaderWithSearch
      iconName={'savedFiltersIconMultiColor'}
      label={'filter.user.savedFilters'}
      useFixedViewBox={16}
      iconCss={muiStyles.headerIcon}
      childrenWrapperStyle={children ? {gap: 8} : undefined}
    >
      <LightIconButton
        iconName='filterIcon'
        useFixedViewBox='0 0 20 16'
        style={{width: 20, height: 16}}
        title={open ? 'layout.filters.closeFilters' : 'layout.filters.openFilters'}
        onClick={toggleOpen}
        selected={open}
        colors={hasActiveFilter && !open ? FILTER_ACTIVE_ICON_COLORS : undefined}
      />
      {children}
    </IconHeaderWithSearch>
  );
};

type UserFilterVars = {userFilter: UserFilterDto};
export const LightSavedFiltersBox: React.FC<Props> = (props) => {
  const {
    open,
    toggleOpen,
    storageIdPrefix,
    component,
    userFiltersLoading,
    addedUserFilters,
    userFilters,
    changeUserFilter,
    filter = {},
    setFilter,
    config,
    limitHeight,
    fitToFilters,
    savedFiltersScrollBoxStyles,
    normalizeForCompare,
    normalizeForStore,
    isPopupMode,
    detachedHeader,
    hasActiveFilter: hasActiveFilterProp,
    onAlertsChanged,
  } = props;
  const isOpen = isPopupMode || open;
  const theme = useTheme();
  const muiStyles = getStyles({theme});
  const formatters = useFormatters();
  const {t} = formatters;
  const filterString = useMemo(() => {
    return toComparableString(filter ?? {}, normalizeForCompare);
  }, [filter, normalizeForCompare]);
  const hasActiveFilter = hasActiveFilterProp ?? false;
  const allUserFilters = useMemo(() => {
    return [...addedUserFilters, ...userFilters];
  }, [addedUserFilters, userFilters]);
  // shared-filters tier (PDW-159): partition into "My filters" / "Shared filters" sections
  const canShare = useCanShareFilters(config);
  const myUserFilters = useMemo(() => allUserFilters.filter((v) => !v.isGlobal), [allUserFilters]);
  const sharedUserFilters = useMemo(() => allUserFilters.filter((v) => Boolean(v.isGlobal)), [allUserFilters]);
  const [myFiltersOpen, _3, setMyFiltersOpen] = useLocalStorage(`${storageIdPrefix}.savedFilters.myOpen`, true);
  const [sharedFiltersOpen, _4, setSharedFiltersOpen] = useLocalStorage(`${storageIdPrefix}.savedFilters.sharedOpen`, true);
  // state
  const [newFilterName, _2, setNewFilterName] = useLocalStorage(`${storageIdPrefix}.newFilterName`, '');
  const [submitLoading, setSubmitLoading] = useState(false);
  const [confirmState, setConfirmState] = useState<SharedFilterConfirmState | null>(null);
  const loading = userFiltersLoading || submitLoading;
  // on response
  const onAlertChanged = useCallback(
    (newUserFilter: UserFilterDto) => {
      changeUserFilter(newUserFilter);
      onAlertsChanged?.();
    },
    [changeUserFilter, onAlertsChanged]
  );
  const onCreateUserFilter = useCallback(
    (response: any) => {
      changeUserFilter(response as UserFilterDto);
      setNewFilterName('');
      setSubmitLoading(false);
    },
    [changeUserFilter, setNewFilterName]
  );
  const onSetDefaultFilter = useCallback(
    (_response: any, vars: UserFilterVars) => {
      const newDefault = !vars.userFilter.isDefault;
      changeUserFilter({...vars.userFilter, isDefault: newDefault});
      setSubmitLoading(false);
    },
    [changeUserFilter]
  );
  // a deleted filter takes its alert along, so the page reloads what shows the alerts too
  const onDeleteUserFilter = useCallback(
    (_response: any, vars: UserFilterVars) => {
      changeUserFilter(vars.userFilter, true);
      setSubmitLoading(false);
      if (vars.userFilter.alertState === 'ACTIVE') onAlertsChanged?.();
    },
    [changeUserFilter, onAlertsChanged]
  );
  const onError = useCallback(() => {
    setSubmitLoading(false);
  }, []);
  // shared-filters tier actions (share / revert-to-private / delete-shared), confirmed via PopupWrapper.
  // Share/revert move data between two rows on the BE (own private row <-> global row of the technical
  // user), so the local state update is two-step: drop the acted-on row, upsert the row from the response
  // (on a name collision the response carries the id of the overwritten counterpart row).
  const onShareUserFilter = useCallback(
    (response: any, vars: UserFilterVars) => {
      changeUserFilter(vars.userFilter, true);
      const globalFilter: UserFilterDto = response && response.id ? response : {...vars.userFilter, isGlobal: true};
      changeUserFilter(globalFilter);
      setSubmitLoading(false);
    },
    [changeUserFilter]
  );
  const onRevertUserFilter = useCallback(
    (response: any, vars: UserFilterVars) => {
      changeUserFilter(vars.userFilter, true);
      const privateFilter: UserFilterDto = response && response.id ? response : {...vars.userFilter, isGlobal: false, sharedBy: undefined};
      changeUserFilter(privateFilter);
      setSubmitLoading(false);
    },
    [changeUserFilter]
  );
  const confirmUserFilterId = confirmState ? String(confirmState.userFilter.id) : '';
  const deleteUserFilterUrl = config.service.url.userFilterDelete.replace('{userFilterId}', confirmUserFilterId);
  const {mutate: mutateDeleteUserFilter} = useApiMutation(
    [deleteUserFilterUrl],
    null,
    {
      reactMutationOptions: {
        onSuccess: onDeleteUserFilter,
        onError,
      },
      axiosConfig: {
        method: 'DELETE',
        url: deleteUserFilterUrl,
      },
    },
    true
  );
  const shareUserFilterUrl = (config.service.url.userFilterShare ?? '').replace('{userFilterId}', confirmUserFilterId);
  const {mutate: mutateShareUserFilter} = useApiMutation(
    [shareUserFilterUrl],
    null,
    {
      reactMutationOptions: {
        onSuccess: onShareUserFilter,
        onError,
      },
      axiosConfig: {
        method: 'POST',
        url: shareUserFilterUrl,
      },
    },
    true
  );
  const revertUserFilterUrl = (config.service.url.userFilterRevertToPrivate ?? '').replace('{userFilterId}', confirmUserFilterId);
  const {mutate: mutateRevertUserFilter} = useApiMutation(
    [revertUserFilterUrl],
    null,
    {
      reactMutationOptions: {
        onSuccess: onRevertUserFilter,
        onError,
      },
      axiosConfig: {
        method: 'POST',
        url: revertUserFilterUrl,
      },
    },
    true
  );
  const deleteSharedUserFilterUrl = (config.service.url.userFilterDeleteShared ?? '').replace('{userFilterId}', confirmUserFilterId);
  const {mutate: mutateDeleteSharedUserFilter} = useApiMutation(
    [deleteSharedUserFilterUrl],
    null,
    {
      reactMutationOptions: {
        onSuccess: onDeleteUserFilter,
        onError,
      },
      axiosConfig: {
        method: 'DELETE',
        url: deleteSharedUserFilterUrl,
      },
    },
    true
  );
  const openSharedFilterConfirm = useCallback((action: SharedFilterConfirmAction, userFilter: UserFilterDto, anchorEl: HTMLElement) => {
    setConfirmState({action, userFilter, anchorEl});
  }, []);
  const closeSharedFilterConfirm = useCallback(() => setConfirmState(null), []);
  const confirmCopy = confirmState ? SHARED_FILTER_CONFIRM_COPY[confirmState.action] : null;
  const createUserFilterUrl = config.service.url.userFilterPost;
  const createUserFilterData: UserFilterCreateDto = useMemo(() => {
    const raw = (filter ?? {}) as Record<string, any>;
    const normalizedForStore = normalizeForStore ? normalizeForStore(raw) : raw;
    return {
      name: newFilterName,
      component,
      data: StringUtils.stringifyStable(omitFilterlessValues(normalizedForStore)),
    };
  }, [newFilterName, component, filter, normalizeForStore]);

  const {mutate: mutateCreateUserFilter} = useApiMutation<UserFilterCreateDto>(
    [createUserFilterUrl, createUserFilterData],
    createUserFilterData,
    {
      reactMutationOptions: {
        onSuccess: onCreateUserFilter,
        onError,
      },
      axiosConfig: {
        data: createUserFilterData,
        method: 'POST',
        url: createUserFilterUrl,
      },
    },
    true
  );
  const onConfirmSharedFilterAction = useCallback(() => {
    if (!confirmState) return;
    const vars = {userFilter: confirmState.userFilter};
    const {action} = confirmState;
    if (action === 'share') mutateShareUserFilter(vars);
    else if (action === 'revert') mutateRevertUserFilter(vars);
    else if (action === 'delete' || action === 'deleteAlert') mutateDeleteUserFilter(vars);
    else mutateDeleteSharedUserFilter(vars);
    setSubmitLoading(true);
    setConfirmState(null);
  }, [confirmState, mutateShareUserFilter, mutateRevertUserFilter, mutateDeleteUserFilter, mutateDeleteSharedUserFilter]);
  // judged on the form before the empty values go: a cleared default (the alerts list's states) is a filter worth saving
  const isCreateDisabled = !newFilterName || Object.keys(omitNullsy(filter ?? {})).length === 0;
  return (
    <div data-name='saved-filters-wrapper' css={muiStyles.savedFiltersBox} style={detachedHeader ? {height: 'auto'} : undefined}>
      {/* When fitToFilters is on, this wrapper is absolutely positioned so the panel's content doesn't
          contribute to the card's height (the card follows the filters only); otherwise it's display:contents
          and has no layout effect. */}
      <div css={fitToFilters && !isPopupMode ? muiStyles.savedFiltersDetached : muiStyles.savedFiltersPassthrough}>
        {!isPopupMode && !detachedHeader && (
          <IconHeaderWithSearch
            iconName={'savedFiltersIconMultiColor'}
            label={'filter.user.savedFilters'}
            useFixedViewBox={16}
            iconCss={muiStyles.headerIcon}
          >
            <LightIconButton
              iconName='filterIcon'
              useFixedViewBox='0 0 20 16'
              style={{width: 20, height: 16}}
              title={open ? 'layout.filters.closeFilters' : 'layout.filters.openFilters'}
              onClick={toggleOpen}
              selected={open}
              colors={hasActiveFilter && !open ? FILTER_ACTIVE_ICON_COLORS : undefined}
            />
          </IconHeaderWithSearch>
        )}
        <div
          css={muiStyles.savedFiltersScrollBox}
          style={{
            minHeight: 16,
            maxHeight: limitHeight ? 133 : undefined,
            ...(detachedHeader ? {overflowY: 'visible' as const, flex: 'none', minHeight: 0} : {}),
            ...(savedFiltersScrollBoxStyles || {}),
          }}
        >
          <Collapse style={{width: '100%', minHeight: 0, maxHeight: 'calc(100% - 36px)'}} in={isOpen} timeout={500}>
            <div css={muiStyles.savedFiltersColumn}>
              <div css={muiStyles.newFilterNameWrapper}>
                <DlMuiTextField
                  variant={'outlined'}
                  label={t('filter.user.newFilterName')}
                  value={newFilterName}
                  onChange={(event) => setNewFilterName(event.target.value ?? '')}
                  InputProps={{
                    endAdornment: (
                      <DlMuiButton
                        title={t('filter.user.save.text')}
                        type='chip-filled'
                        onClick={() => {
                          mutateCreateUserFilter();
                          setSubmitLoading(true);
                        }}
                        disabled={isCreateDisabled}
                      />
                    ),
                  }}
                />
              </div>
              <div css={muiStyles.savedFiltersWrapper}>
                {/* Empty sections are hidden entirely (PDW-159) */}
                {myUserFilters.length > 0 && (
                  <>
                    <div
                      css={muiStyles.groupHeader}
                      onClick={() => setMyFiltersOpen(!myFiltersOpen)}
                      data-name={'dl-mui-saved-filters--my-filters-header'}
                    >
                      <span css={muiStyles.groupHeaderLabel}>
                        <DlMuiDynamicIcon
                          iconName='userIcon'
                          useFixedViewBox='0 0 12 12'
                          noButtonStyles
                          style={{color: 'inherit', padding: 0, margin: 0, pointerEvents: 'none'}}
                          svgIconInputCss={muiStyles.groupHeaderIcon}
                        />
                        {t('filter.user.myFilters')}
                      </span>
                      <DlMuiDynamicIcon
                        iconName='expandMoreIcon'
                        useFixedViewBox='7 8.75 10 6.5'
                        noButtonStyles
                        style={{padding: 0, margin: 0, pointerEvents: 'none', transform: myFiltersOpen ? 'rotate(180deg)' : ''}}
                        inputCss={muiStyles.groupChevron}
                        svgIconInputCss={muiStyles.groupChevronIcon}
                      />
                    </div>
                    <Collapse style={{width: '100%'}} in={Boolean(myFiltersOpen)} timeout={300}>
                      <div css={muiStyles.groupRows}>
                        {myUserFilters.map((v, i) => (
                          <LightSavedFilter
                            key={i}
                            userFilter={v}
                            filterString={filterString}
                            canShare={canShare}
                            onOpenSharedFilterConfirm={openSharedFilterConfirm}
                            onAlertChanged={onAlertChanged}
                            setSubmitLoading={setSubmitLoading}
                            onSetDefaultFilter={onSetDefaultFilter}
                            onError={onError}
                            setFilter={setFilter}
                            formatters={formatters}
                            muiStyles={muiStyles}
                            config={config}
                            normalizeForCompare={normalizeForCompare}
                          />
                        ))}
                      </div>
                    </Collapse>
                  </>
                )}
                {sharedUserFilters.length > 0 && (
                  <>
                    <div
                      css={[muiStyles.groupHeader, muiStyles.groupHeaderShared]}
                      onClick={() => setSharedFiltersOpen(!sharedFiltersOpen)}
                      data-name={'dl-mui-saved-filters--shared-filters-header'}
                    >
                      <span css={muiStyles.groupHeaderLabel}>
                        <DlMuiDynamicIcon
                          iconName='globeIcon'
                          useFixedViewBox='0 0 512 512'
                          noButtonStyles
                          style={{color: 'inherit', padding: 0, margin: 0, pointerEvents: 'none'}}
                          svgIconInputCss={muiStyles.groupHeaderIcon}
                        />
                        {t('filter.user.sharedFilters')}
                      </span>
                      <DlMuiDynamicIcon
                        iconName='expandMoreIcon'
                        useFixedViewBox='7 8.75 10 6.5'
                        noButtonStyles
                        style={{padding: 0, margin: 0, pointerEvents: 'none', transform: sharedFiltersOpen ? 'rotate(180deg)' : ''}}
                        inputCss={muiStyles.groupChevron}
                        svgIconInputCss={muiStyles.groupChevronIcon}
                      />
                    </div>
                    <Collapse style={{width: '100%'}} in={Boolean(sharedFiltersOpen)} timeout={300}>
                      <div css={muiStyles.groupRows}>
                        {sharedUserFilters.map((v, i) => (
                          <LightSavedFilter
                            key={i}
                            userFilter={v}
                            filterString={filterString}
                            canShare={canShare}
                            onOpenSharedFilterConfirm={openSharedFilterConfirm}
                            onAlertChanged={onAlertChanged}
                            setSubmitLoading={setSubmitLoading}
                            onSetDefaultFilter={onSetDefaultFilter}
                            onError={onError}
                            setFilter={setFilter}
                            formatters={formatters}
                            muiStyles={muiStyles}
                            config={config}
                            normalizeForCompare={normalizeForCompare}
                          />
                        ))}
                      </div>
                    </Collapse>
                  </>
                )}
              </div>
            </div>
          </Collapse>
        </div>
      </div>
      <div css={muiStyles.loadingOverlay} data-loading={loading} style={detachedHeader ? {top: 0, height: '100%'} : undefined}>
        <LoadingSpinner isVisible={loading && open} marginTop={0} />
      </div>
      {confirmState && confirmCopy && (
        <PopupWrapper
          openButton={false}
          anchorTag={confirmState.anchorEl}
          onClose={closeSharedFilterConfirm}
          onConfirm={onConfirmSharedFilterAction}
          direction='down'
          yOffset={4}
          showDiscardSaveButtons={!confirmCopy.destructive}
          showKeepDeleteButtons={Boolean(confirmCopy.destructive)}
          cancelTitle='common.confirmDialog.cancel'
          confirmTitle={confirmCopy.confirm}
          contentStyle={{maxWidth: 340}}
          data-name={'dl-mui-saved-filter--confirm-popup'}
        >
          <div css={muiStyles.confirmTitle}>{renderWithFilterName(t(confirmCopy.title), confirmState.userFilter.name)}</div>
          {confirmCopy.paragraph.pre && (
            <div css={muiStyles.confirmText}>{renderWithFilterName(t(confirmCopy.paragraph.pre), confirmState.userFilter.name)}</div>
          )}
          <div css={muiStyles.confirmText}>
            {renderWithFilterName(t(confirmCopy.paragraph.post), confirmState.userFilter.name)}
            {confirmCopy.cannotUndo ? ` ${t('filter.user.cannotUndo')}` : ''}
          </div>
        </PopupWrapper>
      )}
    </div>
  );
};
type LightSavedFilterProps = {
  userFilter: UserFilterDto;
  filterString: string;
  /** Whether the current user holds the Global filter management permission (GLOBAL_FILTER_EDIT_ROLE). */
  canShare: boolean;
  onOpenSharedFilterConfirm: (action: SharedFilterConfirmAction, userFilter: UserFilterDto, anchorEl: HTMLElement) => void;
  /** Replaces the row after its alert was marked, reweighted or retired (PDW-242). */
  onAlertChanged: (userFilter: UserFilterDto) => void;
  setSubmitLoading: (newLoading: boolean) => void;
  onSetDefaultFilter: (data: any, vars: UserFilterVars) => void;
  onError: () => void;
  setFilter: (newFilter: Record<string, any>) => void;
  formatters: Formatters;
  muiStyles: Record<string, SerializedStyles>;
  config: ConfigObjectType;
  normalizeForCompare?: (filter: Record<string, any>) => Record<string, any>;
};
const LightSavedFilter: React.FC<LightSavedFilterProps> = (props) => {
  const {
    userFilter,
    filterString,
    canShare,
    onOpenSharedFilterConfirm,
    onAlertChanged,
    setSubmitLoading,
    onSetDefaultFilter,
    onError,
    setFilter,
    formatters,
    muiStyles,
    config,
    normalizeForCompare,
  } = props;
  const {t} = formatters;
  const isGlobal = Boolean(userFilter.isGlobal);
  // set default filter
  const defaultUserFilterPostUrl = config.service.url.defaultUserFilterPost
    .replace('{componentName}', userFilter.component)
    .replace('{userFilterId}', userFilter.id)
    .replace('{default}', !userFilter.isDefault);
  const {mutate: mutateDefaultUserFilter} = useApiMutation(
    [defaultUserFilterPostUrl],
    null,
    {
      reactMutationOptions: {
        onSuccess: onSetDefaultFilter,
        onError,
      },
      axiosConfig: {
        method: 'POST',
        url: defaultUserFilterPostUrl,
      },
    },
    true
  );
  // every delete is confirmed (PDW-408), worded apart for a filter that is also an alert, which goes with it
  let deleteAction: SharedFilterConfirmAction;
  if (isGlobal) deleteAction = userFilter.alertState === 'ACTIVE' ? 'deleteSharedAlert' : 'deleteShared';
  else deleteAction = userFilter.alertState === 'ACTIVE' ? 'deleteAlert' : 'delete';
  const userFilterComparableString = useMemo(() => {
    try {
      const parsed = JSON.parse(userFilter.data) as Record<string, any>;
      return toComparableString(parsed, normalizeForCompare);
    } catch {
      return userFilter.data;
    }
  }, [userFilter.data, normalizeForCompare]);
  return (
    <div
      css={muiStyles.savedFilterButton}
      data-selected={userFilterComparableString === filterString}
      onClick={() => {
        try {
          setFilter(JSON.parse(userFilter.data));
        } catch {
          // noop
        }
      }}
    >
      <span css={muiStyles.savedFilterName} data-name={'dl-mui-saved-filter--name'}>
        {userFilter.name}
      </span>
      <div css={muiStyles.savedFilterRight}>
        <LightCheckbox
          style={{
            lineHeight: '22px',
          }}
          label={t('filter.user.default')}
          value={userFilter.isDefault}
          onChange={() => {
            mutateDefaultUserFilter({userFilter});
            setSubmitLoading(true);
          }}
          data-name={'dl-mui-saved-filter--checkbox'}
        />
        <span css={muiStyles.savedFilterRightBox}>
          <LightSavedFilterAlertBell
            userFilter={userFilter}
            config={config}
            formatters={formatters}
            onAlertChanged={onAlertChanged}
            setSubmitLoading={setSubmitLoading}
            onError={onError}
          />
          {isGlobal && !canShare && (
            <DlMuiDynamicIcon
              iconName='lockSolidIcon'
              useFixedViewBox='0 0 448 512'
              iconTitle='filter.user.sharedReadOnly'
              style={{
                color: 'inherit',
                padding: 0,
                margin: 0,
              }}
              inputCss={muiStyles.trashIconButton}
              svgIconInputCss={muiStyles.lockIcon}
              noButtonStyles
              data-name={'dl-mui-saved-filter--lock-icon'}
            />
          )}
          {!isGlobal && canShare && (
            <DlMuiDynamicIcon
              iconName='globeIcon'
              useFixedViewBox='0 0 512 512'
              iconTitle='filter.user.share.tooltip'
              style={{
                color: 'inherit',
                padding: 0,
                margin: 0,
              }}
              inputCss={muiStyles.trashIconButton}
              svgIconInputCss={muiStyles.shareIcon}
              noButtonStyles
              onClick={(event) => {
                event.preventDefault();
                event.stopPropagation();
                onOpenSharedFilterConfirm('share', userFilter, event.currentTarget);
              }}
              data-name={'dl-mui-saved-filter--share-icon'}
            />
          )}
          {isGlobal && canShare && (
            <DlMuiDynamicIcon
              iconName='globeSlashIcon'
              useFixedViewBox='0 0 640 512'
              iconTitle='filter.user.revert.tooltip'
              style={{
                color: 'inherit',
                padding: 0,
                margin: 0,
              }}
              inputCss={muiStyles.trashIconButton}
              svgIconInputCss={muiStyles.shareRevertIcon}
              noButtonStyles
              onClick={(event) => {
                event.preventDefault();
                event.stopPropagation();
                onOpenSharedFilterConfirm('revert', userFilter, event.currentTarget);
              }}
              data-name={'dl-mui-saved-filter--revert-icon'}
            />
          )}
          {(!isGlobal || canShare) && (
            <DlMuiDynamicIcon
              iconName='trashIcon'
              useFixedViewBox='0 0 12 12'
              iconTitle={`filter.user.${deleteAction}.tooltip`}
              style={{
                color: 'inherit',
                padding: 0,
                margin: 0,
              }}
              inputCss={muiStyles.trashIconButton}
              svgIconInputCss={muiStyles.trashIcon}
              noButtonStyles
              onClick={(event) => {
                event.preventDefault();
                event.stopPropagation();
                onOpenSharedFilterConfirm(deleteAction, userFilter, event.currentTarget);
              }}
              data-name={'dl-mui-saved-filter--trash-icon'}
            />
          )}
        </span>
      </div>
    </div>
  );
};

const getStyles: GetStylesFunction = (_options) => ({
  savedFiltersBox: css`
    position: relative;
    flex: 1 1 0;
    width: 100%;
    max-width: 292px;
    height: 100%;
    display: flex;
    flex-direction: column;
    & .MuiCollapse-wrapper {
      height: 100%;
    }
  `,
  // No layout effect — the header + scroll box participate exactly as before (default behaviour).
  savedFiltersPassthrough: css`
    display: contents;
  `,
  // fitToFilters: take the content out of flow so the panel doesn't grow the card; it fills the box (which is
  // stretched to the filters height by the row's align-items:stretch) and scrolls internally.
  savedFiltersDetached: css`
    position: absolute;
    inset: 0;
    display: flex;
    flex-direction: column;
  `,
  headerIcon: css`
    width: 16px;
    height: 16px;
  `,
  loadingOverlay: css`
    position: absolute;
    top: 32px;
    left: 0;
    width: 100%;
    height: calc(100% - 32px);
    overflow: hidden;
    background: rgba(0, 0, 0, 0.15);
    &[data-loading='false'] {
      display: none;
    }
    & > div {
      height: 100%;
      display: flex;
      flex-direction: column;
      justify-content: center;
    }
    & .fa-circle-notch {
      font-size: 64px;
    }
  `,
  /* No inline padding: the content (name field, filter rows) must share the left edge with the
     "Saved filters" header icon above it; row borders/outlines are box-inset so nothing clips. */
  savedFiltersScrollBox: css`
    flex: 1;
    overflow-y: auto;
  `,
  savedFiltersColumn: css`
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    align-items: flex-start;
  `,
  newFilterNameWrapper: css`
    width: 100%;
    & > div {
      margin: 8px 0;
      height: 38px;
      width: 100% !important;
    }
    & .MuiInputBase-root {
      border-radius: 8px;
      padding-right: 8px;
    }
  `,
  savedFiltersWrapper: css`
    margin: 0 0 8px 0;
    width: 100%;
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    align-items: flex-start;
    gap: 8px;
  `,
  savedFilterButton: css`
    width: 100%;
    padding: 4px 12px;
    border-radius: 8px;
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 8px;
    border: 1px solid transparent;
    background: var(--dlSavedFilterButtonBackground);
    color: var(--dlSavedFilterButton);
    --lightCheckbox: var(--dlSavedFilterButton);
    /* the row's icons (bell of a filter that is not an alert, lock, share, delete, the Default circle): MERIE's grey
       as the design, not the row's dark text colour, unchanged when hovered; HAIDI's dark blue, darker when the icon
       is hovered (an app without the hover var keeps the icon's colour) */
    --dlSavedFilterIcon: var(--dlSavedFilterButtonIcon);
    --dlSavedFilterIconHover: var(--dlSavedFilterButtonIconHover, var(--dlSavedFilterButtonIcon));
    --lightCheckboxIcon: var(--dlSavedFilterIcon);
    --lightCheckboxIconHover: var(--dlSavedFilterIconHover);
    /* the light grey "Default" of an unset default was barely visible */
    --lightCheckboxLabelOpacity: 0.6;
    font-family: var(--dlFontFamilySemiBold);
    line-height: 22px;
    &:hover {
      border-color: var(--dlSavedFilterButtonHoverBorder);
      background: var(--dlSavedFilterButtonHoverBackground);
      color: var(--dlSavedFilterButtonHover);
      --lightCheckbox: var(--dlSavedFilterButtonHover);
    }
    &[data-selected='true'] {
      border-color: var(--dlSavedFilterButtonSelectedBorder);
      background: var(--dlSavedFilterButtonSelectedBackground);
      color: var(--dlSavedFilterButtonSelected);
      --lightCheckbox: var(--dlSavedFilterButtonSelected);
      /* MERIE's selected row is dark, where grey would not stand out; HAIDI's is light */
      --dlSavedFilterIcon: var(--dlSavedFilterButtonSelectedIcon);
      --dlSavedFilterIconHover: var(--dlSavedFilterButtonSelectedIconHover, var(--dlSavedFilterButtonSelectedIcon));
      &:hover {
        background: var(--dlSavedFilterButtonSelectedHoverBackground);
      }
    }
    &:active {
      background: var(--dlSavedFilterButtonPressedBackground);
    }
  `,
  savedFilterName: css`
    font-family: var(--dlFontFamilySemiBold);
    overflow-wrap: anywhere;
    word-break: break-word;
    user-select: none;
  `,
  savedFilterRight: css`
    display: inline-flex;
    justify-content: flex-start;
    align-items: flex-start;
    gap: 8px;
  `,
  savedFilterRightBox: css`
    align-self: stretch;
    display: inline-flex;
    justify-content: center;
    align-items: center;
    gap: 8px;
  `,
  trashIconButton: css`
    color: var(--dlSavedFilterIcon) !important;
    &:hover {
      color: var(--dlSavedFilterIconHover) !important;
    }
  `,
  trashIcon: css`
    font-size: 14px;
  `,
  shareIcon: css`
    font-size: 13px;
  `,
  /* globeSlashIcon has a 640x512 viewBox (globe + slash), so it needs a 1.25x wider box to render the
     globe at the same height as the 512x512 globeIcon used in the "My filters" rows */
  shareRevertIcon: css`
    font-size: 13px;
    width: 1.25em;
  `,
  lockIcon: css`
    font-size: 12px;
  `,
  groupHeader: css`
    width: 100%;
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 7px;
    cursor: pointer;
    user-select: none;
    padding: 2px;
    font-family: var(--dlFontFamilySemiBold);
    font-size: 11px;
    letter-spacing: 0.09em;
    text-transform: uppercase;
    color: var(--dlTheme-500);
  `,
  groupHeaderShared: css`
    /* HAIDI collapses --dlThemeGray-100 into --dlThemeGray-50, so that line is invisible there;
       apps that want a lighter divider set --dlSavedFilterGroupDivider (see variables.less) */
    border-top: 1px solid var(--dlSavedFilterGroupDivider, var(--dlThemeGray-300));
    margin-top: 4px;
    padding-top: 10px;
    /* the divider belongs between the sections - when the user has no private filters
       and the shared section renders first, drop it and keep the plain header spacing */
    &:first-child {
      border-top: none;
      margin-top: 0;
      padding-top: 2px;
    }
  `,
  groupHeaderLabel: css`
    display: inline-flex;
    align-items: center;
    gap: 7px;
  `,
  groupHeaderIcon: css`
    font-size: 12px;
  `,
  groupChevron: css`
    color: inherit !important;
  `,
  groupChevronIcon: css`
    width: 12px;
    height: 8px;
    display: block;
  `,
  groupRows: css`
    width: 100%;
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    align-items: flex-start;
    gap: 8px;
  `,
  confirmTitle: css`
    font-family: var(--dlFontFamilySemiBold);
    font-size: 15px;
    color: var(--dlThemeGray-900);
    padding: 0 4px;
  `,
  confirmText: css`
    font-size: 13px;
    line-height: 1.5;
    color: var(--dlThemeGray-700);
    padding: 0 4px;
    & strong {
      color: var(--dlThemeGray-800);
    }
  `,
});
